/**
 * Política de autenticação do tenant (B15, decisão A2, §13.1).
 *
 * ── Por que `exigido` e `exigido_no_provedor` são campos separados ─────────
 *
 * Parece redundância e não é. `exigido` é a intenção registrada da revenda;
 * `exigido_no_provedor` é o que o realm exige AGORA. Eles divergem num caso
 * real: reprovisionar o realm (G01 recriando, ou `keycloak:provisionar` depois
 * de uma restauração) aplica o template, que nasce desligado por decisão da A2.
 *
 * Sem os dois campos, a tela mostraria "MFA exigido" enquanto ninguém é
 * desafiado — e a revenda descobriria num incidente. Com os dois, a divergência
 * aparece e alguém reaplica.
 *
 * ── A justificativa é obrigatória nas duas rotas ───────────────────────────
 *
 * Ligar MFA obriga TODO usuário do tenant a cadastrar TOTP no próximo login:
 * numa revenda com dez atendentes, são dez pessoas paradas na segunda de manhã.
 * Resetar o segundo fator de alguém é o vetor de sequestro de conta que a §13.1
 * nomeia. Nos dois casos o que fica registrado é quem decidiu e por quê — e o
 * banco recusa justificativa com menos de dez caracteres, porque *"justificativa
 * vazia é pior que ausente: parece preenchida numa auditoria"*.
 */

import { z } from 'zod';
import { IdSchema } from './comum.js';

/** Dez caracteres é o mínimo do CHECK `audit_justificativa_real` (migração 008). */
const JustificativaSchema = z.string().trim().min(10).max(1000);

export const EstadoDoMfaSchema = z.object({
  exigido: z.boolean(),
  exigido_no_provedor: z.boolean(),
});

export type EstadoDoMfa = z.infer<typeof EstadoDoMfaSchema>;

export const DefinirExigenciaDeMfaSchema = z.object({
  exigir: z.boolean(),
  justificativa: JustificativaSchema,
});

export type DefinirExigenciaDeMfa = z.infer<typeof DefinirExigenciaDeMfaSchema>;

export const ResetarSegundoFatorSchema = z.object({
  justificativa: JustificativaSchema,
});

export type ResetarSegundoFator = z.infer<typeof ResetarSegundoFatorSchema>;

export const ResultadoDoResetSchema = z.object({
  usuario_id: IdSchema,
  credenciais_removidas: z.number().int(),
  /**
   * `false` quando não havia segundo fator para remover.
   *
   * A tela NÃO deve dizer "resetado" nesse caso: quem pediu o reset achava que
   * a pessoa tinha MFA, e não tinha — o que costuma significar que o problema
   * de acesso é outro, e insistir no reset não resolve.
   */
  havia_segundo_fator: z.boolean(),
});

export type ResultadoDoReset = z.infer<typeof ResultadoDoResetSchema>;

/**
 * Pedido de concessão de step-up (B17, §13.1).
 *
 * O `alvo` é opcional no schema e obrigatório em algumas ações — quem sabe
 * quais é o catálogo do servidor, não este arquivo. Duplicar a regra aqui
 * criaria duas listas que discordam no dia em que alguém acrescentar uma ação.
 */
export const PedirStepUpSchema = z
  .object({
    /** Uma das ações sensíveis da §13.1. O servidor recusa qualquer outra. */
    acao: z.string().min(1),
    /**
     * Sobre QUAL recurso. Concessão sem alvo liberaria todos os outros do mesmo
     * tipo — step-up para exportar um cliente não pode autorizar exportar outro.
     */
    alvo: z.string().min(1).nullish(),
    /**
     * OPCIONAL no contrato, e obrigatória por AÇÃO.
     *
     * O `min(10)` daqui recusava o pedido antes de o servidor consultar o
     * catálogo — então uma ação que não exige justificativa ficava
     * impossível de pedir, mesmo com a regra correta do outro lado.
     *
     * Quem decide continua sendo `exigeJustificativa` na definição da ação:
     * ação que exige e vem sem texto é recusada lá, com o motivo. O
     * contrato não sabe quais ações exigem — e não deve saber, senão a
     * lista teria de ser mantida em dois lugares.
     *
     * O teto NÃO entra aqui: o contrato anterior não tinha um, e acrescentá-lo
     * agora recusaria um cliente que hoje manda texto longo e passa. Quem
     * limita tamanho é o servidor, onde a mudança não quebra ninguém.
     */
    justificativa: z.string().optional(),
  })
  .describe('Pedido de acesso elevado temporário');

export const ConcessaoDeStepUpSchema = z
  .object({
    id: z.string().uuid(),
    /** `acao` ou `acao:alvo`. É o que a verificação compara, exato. */
    escopo: z.string(),
    expira_em: z.string().datetime(),
  })
  .describe('Concessão vigente de acesso elevado');

export type PedirStepUp = z.infer<typeof PedirStepUpSchema>;
export type ConcessaoDeStepUp = z.infer<typeof ConcessaoDeStepUpSchema>;
